"use client";
import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getTotalCartCount,
  getCartPreviousAddedDate,
} from "@/redux/order/orderSlice";
import { usefetchDataFromApi } from "@/utils/api";

const RETRY_DELAY_MS = 3000;

/**
 * Centralized hook for fetching & syncing cart count.
 * - Auto-fetches on mount and whenever `cartVersion` changes.
 * - Deduplicates: skips if a fetch is already in-flight.
 * - Retries once on failure after a delay.
 * - Returns `refetchCart` for imperative calls (e.g. after logout).
 */
const useCartCount = () => {
  const dispatch = useDispatch();
  const { cartVersion } = useSelector((state) => state.order);
  const inFlightRef = useRef(false);

  const fetchCart = useCallback(
    (retry = true) => {
      if (inFlightRef.current) return; // deduplicate
      inFlightRef.current = true;

      usefetchDataFromApi({
        url: "cart/index",
        method: "POST",
        data: { platform: 3, count: true },
      })
        .then((res) => {
          if (res.success) {
            const { data, start } = res.data;
            dispatch(getTotalCartCount(data));
            if (start !== undefined) {
              dispatch(getCartPreviousAddedDate(start));
            }
          } else if (res.data?.data !== undefined) {
            dispatch(getTotalCartCount(res.data.data));
          } else if (retry) {
            setTimeout(() => fetchCart(false), RETRY_DELAY_MS);
          }
        })
        .catch(() => {
          if (retry) {
            setTimeout(() => fetchCart(false), RETRY_DELAY_MS);
          }
        })
        .finally(() => {
          inFlightRef.current = false;
        });
    },
    [dispatch]
  );

  useEffect(() => {
    fetchCart();
  }, [cartVersion, fetchCart]);

  return fetchCart;
};

export default useCartCount;

